import { SHARE_RECORDS_STATUS } from "../common/Constant";
import { handleApiError } from "../utils/apiUtils";
import { preventDuplicateRequest } from "../utils/requestManager";
import api from "./Api";

const Case = process.env.REACT_APP_CASE_URL || "";
if (!Case) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables."
  );
}

const caseAPI = api(Case);

export interface ApiResponse<T> {
  id?: number;
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

export interface RecordPayload {
  caseInfoId: string | number | null;
  documentCategory: string | number | null;
  documentTypeId: string | number | null;
}

export interface ShowFilesListResult {
  tenantId: number;
  caseInfoId: number;
  caseRecordHeaderId: number;
  documentTypeId: number;
  documentTypeName: string;
  documentCategory: string;
  caseRecordDetailsId: number;
  fileName: string;
  cloudLocation: string;
  description: string;
  uploadedByName: string;
  detailCreatedDate: string;
}

interface ShareRecordDetails {
  shareId: number;
  caseInfoId: number | null;
  sharedByUserId: number | null;
  sharingNote: string;
  shareType: string;
  expiryDate: string | null;
  sharedToUserIds: string | null;
  sharedToEmails: string | null;
  caseRecordDetailsIds: string;
  createdBy: number | null;
  modifiedBy: number | null;
}

interface CaseIDList {
  tenantId: number;
  caseInfoId: number;
  caseSubjectId: number;
  caseId: string;
  claimNumber: string;
  subjectFullName: string;
}


const fetchCaseIDList = async (search: number | string): Promise<ApiResponse<CaseIDList>> => {
  try {
    const response = await caseAPI.get(
      `/api/share-record/case/list?searchString=${search}`
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const showFilesList = async (
  formData: RecordPayload
): Promise<ApiResponse<ShowFilesListResult>> => {
  try {
    const response = await caseAPI.post<ApiResponse<ShowFilesListResult>>(
      "/api/share-record/case/record/list",
      formData
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getSharedByMeList = async (
  sharedbyUserId: number | null,
  searchString?: string,
): Promise<any> => {
  const requestKey = `getSharedByMeList_${sharedbyUserId}_${searchString || ""}`;

  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await caseAPI.get(
        `/api/share-record/sharedbyme?sharedByUserId=${sharedbyUserId}&searchString=${searchString}`,
      );

      const updatedResult = response.data?.result.map((item: any) => ({
        ...item,
        actions: {
          [item.shareStatus === SHARE_RECORDS_STATUS.ACTIVE ? "Edit" : "View"]:
            item.shareStatus === SHARE_RECORDS_STATUS.ACTIVE ? "Edit" : "View",
        },
      }));

      return updatedResult;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  });
};

const getSharedWithMeList = async (
  sharedToUserId: number | null,
  searchString?: string,
): Promise<any> => {
  const requestKey = `getSharedByMeList_${sharedToUserId}_${searchString || ""}`;

  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await caseAPI.get(
        `/api/share-record/sharedtome?sharedToUserId=${sharedToUserId}&searchString=${searchString}`,
      );

      const updatedResult = response.data?.result.map((item: any) => ({
        ...item,
        actions: {
          View: "View",
        },
      }));

      return updatedResult;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  });
};

const getShareRecordDetails = async (
  shareId: number | null,
): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/share-record/details?shareId=${shareId}`
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const addShareRecordsDetails = async (
  userData: ShareRecordDetails
): Promise<ApiResponse<number | null>> => {
  try {
    const response = await caseAPI.post<ApiResponse<number | null>>(
      "/api/share-record/add",
      userData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const updateShareRecordsDetails = async (
  userData: ShareRecordDetails
): Promise<ApiResponse<number | null>> => {
  try {
    const response = await caseAPI.post<ApiResponse<number | null>>(
      "/api/share-record/update",
      userData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const revokeShareRecord = async (
  shareId: number | null,
): Promise<ApiResponse<number | null>> => {
  try {
    const response = await caseAPI.post(`/api/share-record/revoke?shareId=${shareId}`);
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const updateShareRecordStatus = async (
  shareId: number | null,
): Promise<ApiResponse<number | null>> => {
  try {
    const response = await caseAPI.post(`/api/share-record/status/update?shareId=${shareId}`);
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getShareRecordDownloadHistory = async (
  caserecorddetailsId: number,
  shareId: number,
): Promise<ApiResponse<number | null>> => {
  const params = {
    shareId: shareId,
    caseRecordDetailsID: caserecorddetailsId,
  };

  try {
    const response = await caseAPI.post(
      "/api/share-record/download/history",
      params,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export {
  fetchCaseIDList,
  showFilesList,
  addShareRecordsDetails,
  getSharedWithMeList,
  getSharedByMeList,
  updateShareRecordsDetails,
  getShareRecordDetails,
  revokeShareRecord,
  updateShareRecordStatus,
  getShareRecordDownloadHistory,
};
